import { logger, safeError } from '../../../utils/logger';
import { useState, useEffect, useRef } from 'react';
import { createEmbeddingContext } from 'amazon-quicksight-embedding-sdk';
import { ReporteService } from '../../../services/ReporteService';
import { Spinner, Alert } from 'react-bootstrap';

/**
 * Panel del chat Q&A Generativo (Amazon Q) de QuickSight.
 * Pensado para ir al costado del dashboard (ver ReporteGestion).
 * Se embebe con el SDK usando embedGenerativeQnA.
 */
export const ConsultasInteligentes = () => {
  const qnaRef = useRef<HTMLDivElement>(null);
  const [qnaUrl, setQnaUrl] = useState<string | null>(null);
  const [embeddingContext, setEmbeddingContext] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 1. Obtener la URL del backend
  useEffect(() => {
    const fetchUrl = async () => {
      try {
        setLoading(true);
        const data = await ReporteService.getUrlQna();
        if (data && data.embedUrl) {
          setQnaUrl(data.embedUrl);
        } else {
          setError('No se recibió una URL válida para el chat de consultas.');
        }
      } catch (err) {
        setError('Error al conectar con el servicio de consultas.');
      } finally {
        setLoading(false);
      }
    };

    fetchUrl();
  }, []);

  // 2. Crear el contexto de embedding cuando tenemos la URL
  useEffect(() => {
    if (qnaUrl) {
      const createContext = async () => {
        try {
          const context = await createEmbeddingContext();
          setEmbeddingContext(context);
        } catch (err) {
          logger.error('Error creando contexto de embedding (Q&A):', safeError(err));
        }
      };
      createContext();
    }
  }, [qnaUrl]);

  // 3. Incrustar el chat Q&A cuando el contexto está listo
  useEffect(() => {
    if (embeddingContext && qnaRef.current && qnaUrl) {
      const embed = async () => {
        const frameOptions = {
          url: `${qnaUrl}&locale=es-AR`,
          container: qnaRef.current,
          height: '100%',
          width: '100%',
        };

        await embeddingContext.embedGenerativeQnA(frameOptions, {});
      };
      embed();
    }
  }, [embeddingContext, qnaUrl]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        border: '1px solid #dee2e6',
        borderRadius: '4px',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          background: '#232f3e',
          color: '#fff',
          padding: '10px 15px',
          fontWeight: 'bold',
          flex: '0 0 auto',
        }}
      >
        Consultas Inteligentes
      </div>
      <div style={{ flex: '1 1 auto', minHeight: 0, position: 'relative' }}>
        {loading && (
          <div className="text-center mt-3">
            <Spinner animation="border" variant="primary" size="sm" />
          </div>
        )}
        {error && (
          <Alert variant="warning" className="m-2">
            {error}
          </Alert>
        )}
        <div
          ref={qnaRef}
          style={{
            height: '100%',
            width: '100%',
            display: (loading || error) ? 'none' : 'block',
          }}
        />
      </div>
    </div>
  );
};
